import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal, Overline } from "@/components/Reveal";
import { SITE, IMAGES } from "@/config/site";

const SECTIONS = [
    {
        id: "ambiente",
        title: "Ambiente",
        text: "Una stanza raccolta, luce naturale filtrata, legno e tessuti che invitano al riposo.",
        image: IMAGES.candles,
        alt: "Candele e tessuti naturali nello studio",
    },
    {
        id: "atmosfera",
        title: "Atmosfera",
        text: "Candele, profumi discreti e silenzio: ogni dettaglio è pensato per abbassare il ritmo.",
        image: IMAGES.ambiance,
        alt: "Atmosfera rilassante dello studio",
    },
    {
        id: "accoglienza",
        title: "Accoglienza",
        text: "Si arriva, ci si siede, si respira. Un momento di tisana e ascolto prima di ogni trattamento.",
        image: IMAGES.session,
        alt: "Momento di accoglienza nello studio",
    },
];

const Studio = () => (
    <div data-testid="studio-page">
        <SEO
            title="Lo Studio Olistico a Bologna | Sara Solferini"
            description="Uno spazio intimo nel cuore di Bologna: ambiente, atmosfera e accoglienza dello studio olistico. Visita la galleria e trova la posizione."
        />

        <header className="mx-auto max-w-6xl px-5 pb-16 pt-36 md:px-8 md:pt-44">
            <Reveal>
                <Overline>Lo studio</Overline>
                <h1 className="mt-5 max-w-2xl font-serif text-4xl font-light tracking-tight text-ink sm:text-5xl lg:text-6xl" data-testid="studio-title">
                    Uno spazio che <span className="italic text-sage">rallenta il tempo</span>
                </h1>
                <p className="mt-7 max-w-xl text-base leading-relaxed text-clay">
                    Nel cuore di Bologna, un luogo piccolo e silenzioso, pensato per accogliere una persona
                    alla volta. Queste immagini raccontano lo spirito dello spazio.
                </p>
            </Reveal>
        </header>

        <section className="mx-auto max-w-6xl space-y-20 px-5 pb-24 md:px-8" data-testid="studio-gallery">
            {SECTIONS.map((s, i) => (
                <Reveal key={s.id}>
                    <div
                        className={`grid items-center gap-10 md:grid-cols-2 md:gap-16 ${
                            i % 2 === 1 ? "md:[&>*:first-child]:order-2" : ""
                        }`}
                        data-testid={`studio-section-${s.id}`}
                    >
                        <div className="overflow-hidden rounded-2xl">
                            <img
                                src={s.image}
                                alt={s.alt}
                                loading="lazy"
                                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 md:h-[420px]"
                            />
                        </div>
                        <div>
                            <span className="font-serif text-5xl font-light text-beige">0{i + 1}</span>
                            <h2 className="mt-4 font-serif text-3xl font-normal text-ink">{s.title}</h2>
                            <p className="mt-4 max-w-md text-base leading-relaxed text-clay">{s.text}</p>
                        </div>
                    </div>
                </Reveal>
            ))}

            <Reveal>
                <div className="grid items-center gap-10 md:grid-cols-2 md:gap-16" data-testid="studio-section-posizione">
                    <div>
                        <span className="font-serif text-5xl font-light text-beige">04</span>
                        <h2 className="mt-4 font-serif text-3xl font-normal text-ink">Posizione</h2>
                        <p className="mt-4 max-w-md text-base leading-relaxed text-clay">
                            {SITE.address}. La zona è servita dai mezzi pubblici e facilmente raggiungibile
                            dal centro.
                        </p>
                        <Link
                            to="/contatti"
                            data-testid="studio-link-contatti"
                            className="group mt-7 inline-flex items-center gap-2 text-sm font-medium uppercase tracking-widest text-sage"
                        >
                            <MapPin className="h-4 w-4" strokeWidth={1.5} />
                            Vedi contatti e indicazioni
                            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1.5} />
                        </Link>
                    </div>
                    <div className="overflow-hidden rounded-2xl shadow-[0_4px_20px_rgba(67,46,30,0.08)]">
                        <iframe
                            title="Mappa della zona dello studio a Bologna"
                            src={SITE.mapsEmbed}
                            className="h-72 w-full border-0 md:h-[380px]"
                            loading="lazy"
                            data-testid="studio-map"
                        />
                    </div>
                </div>
            </Reveal>
        </section>
    </div>
);

export default Studio;
